import BindingContext, { ENUM_KEY_PROP_NAME, IEntity } from "@odata/BindingContext";
import { ODataError } from "@odata/Data.types";
import {
    EntitySetName,
    IPrEmployeeEntity,
    IPrPayslipEmploymentEntity,
    IPrPayslipEntity,
    PrPayslipEmploymentEntity,
    PrPayslipEmploymentExtraSalaryEntity,
    PrPayslipEmploymentRetirementSavingEntity,
    PrPayslipEmploymentSalaryComponentEntity,
    PrPayslipEntity
} from "@odata/GeneratedEntityTypes";
import { PrExtraSalaryComputationTypeCode, PrRegularSalaryTypeCode, PrSalaryComponentTypeCode } from "@odata/GeneratedEnums";
import { BatchRequest, isBatchResultOk } from "@odata/OData";
import { isDefined } from "@utils/general";
import { logger } from "@utils/log";
import i18next from "i18next";
import { getAlertFromError } from "views/formView/Form.utils";
import { FormStorage } from "views/formView/FormStorage";

import { BulkAddItemType } from "../Payslip.utils";
import { commonBulkAddFieldPaths, getCollectionPath, getImplicitFields } from "./BulkAddPayslipItems.def";

const BULK_ADD_ATOMICITY_GROUP = "bulkAddPayslipItems";

export const TEMPLATE_EMPLOYMENT_ID = -1;
export const EMPLOYEES_TEMPLATE_ID = -2;

type UpdatePayslipsPayloadEntry = {
    payslipId: string;
    employmentId: number;
    payslipItem: IEntity;
}
type UpdatePayslipsArgs = {
    data: UpdatePayslipsPayloadEntry[]
    itemType: BulkAddItemType
    storage: FormStorage
    payslipTargets?: IBulkAddPayslipTarget[]
}

export const isCommonEmployment = (empl: IPrPayslipEmploymentEntity): boolean => empl.Id === -1;

export interface IBulkAddPayslipTargetEmployment {
    payslipEmploymentId: number;
    number: string;
    name: string;
    isCompensation: boolean;
}

export interface IBulkAddPayslipTarget {
    payslipId: number;
    etag?: string;
    employmentIds: number[];
    compensationEmploymentIds: number[];
    employee?: IPrEmployeeEntity;
    employments?: IBulkAddPayslipTargetEmployment[];
}

type PreparePayloadDataArgs = {
    data: Partial<IPrPayslipEntity>;
    bindingContext: BindingContext;
    itemType: BulkAddItemType;
}

const NAVIGATION_BINDING_ENTITY_SETS: Partial<Record<string, EntitySetName>> = {
    [PrPayslipEmploymentRetirementSavingEntity.RetirementSavingsProduct]: EntitySetName.PrRetirementSavingsProducts,
};

// TODO: Replace this request-specific enum flattening -- temp solution
const cleanBulkPayslipItemEnumReferences = <T extends IEntity>(entity: T, bindingContext: BindingContext): T => {
    const cleanedEntity: IEntity = { ...entity };

    for (const [key, value] of Object.entries(cleanedEntity)) {
        if (!bindingContext.isValidNavigation(key)) {
            continue;
        }

        const propertyBc = bindingContext.navigate(key);
        if (!propertyBc.isEnum()) {
            continue;
        }

        const code = (value as IEntity)?.[ENUM_KEY_PROP_NAME];
        if (isDefined(code)) {
            cleanedEntity[`${key}${ENUM_KEY_PROP_NAME}`] = code;
        }
        delete cleanedEntity[key];
    }

    return cleanedEntity as T;
};

const cleanEntity = (entity: IEntity, bindingContext: BindingContext, initialData: IEntity = {}): IEntity => {
    const cleanedEntity = Object.entries(entity).reduce<IEntity>(
        (acc, [key, value]) => {
            if (key === BindingContext.NEW_ENTITY_ID_PROP || BindingContext.isLocalContextPath(key)) {
                return acc;
            }

            const entitySet = NAVIGATION_BINDING_ENTITY_SETS[key];
            const id = entitySet ? (value as IEntity)?.Id : undefined;
            acc[key] = id != null ? { "@odata.id": `${entitySet}(${id})` } : value;

            return acc;
        },
        { ...initialData }
    );

    return cleanBulkPayslipItemEnumReferences(cleanedEntity, bindingContext);
};

const getEmploymentLabel = (payslipEmploymentId: number, payslipTargets: IBulkAddPayslipTarget[]): string => {
    for (const target of payslipTargets) {
        const employment = target.employments?.find(employment => employment.payslipEmploymentId === payslipEmploymentId);
        if (employment) {
            return [target.employee?.NumberOurs, employment.name].filter(Boolean).join(" – ");
        }
    }

    return "";
};

const getEmploymentValidationMessage = (error: ODataError, payslipTargets: IBulkAddPayslipTarget[]): string[] => {
    if (!error._validationMessages?.length) {
        const subTitle = getAlertFromError(error).subTitle;
        if (Array.isArray(subTitle)) {
            return subTitle.filter((message): message is string => typeof message === "string");
        }
        return typeof subTitle === "string" ? [subTitle] : [];
    }

    return error._validationMessages.map(validationMessage => {
        if (validationMessage.entity?.entityType !== "PrPayslipEmployment") {
            return validationMessage.message;
        }

        const employmentLabel = getEmploymentLabel(validationMessage.entity.id, payslipTargets);
        return employmentLabel ? `${employmentLabel}: ${validationMessage.message}` : validationMessage.message;
    });
};

const hasDifferentErrorCodes = (errors: ODataError[]): boolean => new Set(errors.map(error => error._code)).size > 1;

const getBulkAddErrorAlert = (errors: ODataError[], payslipTargets: IBulkAddPayslipTarget[]) => {
    const alert = getAlertFromError(errors[0]);
    if (hasDifferentErrorCodes(errors)) {
        alert.title = i18next.t("Common:Errors.ErrorHappened");
    }
    alert.detailData = { errors };
    alert.subTitle = Array.from(new Set(errors.flatMap(error => getEmploymentValidationMessage(error, payslipTargets))));
    return alert;
};

const isHourlyExtraSalaryForCompensation = (item: IEntity): boolean => {
    const computationType = (item[PrPayslipEmploymentExtraSalaryEntity.ComputationType] as IEntity)?.Code;
    if (![
        PrExtraSalaryComputationTypeCode.AbsoluteAmountHour,
        PrExtraSalaryComputationTypeCode.AverageHourlySalaryPercentage,
    ].includes(computationType as PrExtraSalaryComputationTypeCode)) {
        return false;
    }

    const selectedEmploymentId = item[commonBulkAddFieldPaths.Employment];
    const employments = item[commonBulkAddFieldPaths.Employments] as IBulkAddPayslipTargetEmployment[];
    return employments?.some(employment => employment.payslipEmploymentId === selectedEmploymentId && employment.isCompensation) ?? false;
};

const isHourlySalaryComponentForCompensation = (item: IEntity): boolean => {
    const type = (item[PrPayslipEmploymentSalaryComponentEntity.Type] as IEntity)?.Code;
    const regularSalaryType = (item[PrPayslipEmploymentSalaryComponentEntity.RegularSalaryType] as IEntity)?.Code;
    if (type !== PrSalaryComponentTypeCode.Regular || regularSalaryType !== PrRegularSalaryTypeCode.Hourly) {
        return false;
    }

    const selectedEmploymentId = item[commonBulkAddFieldPaths.Employment];
    const employments = item[commonBulkAddFieldPaths.Employments] as IBulkAddPayslipTargetEmployment[];
    return employments?.some(employment => employment.payslipEmploymentId === selectedEmploymentId && employment.isCompensation) ?? false;
};

export const preparePayloadData = (args: PreparePayloadDataArgs) => {
    const { data, bindingContext, itemType } = args;
    const employmentHolders = data.Employments;
    const employeeEmploymentHolder = employmentHolders.find((empl) => !isCommonEmployment(empl));


    const collectionPath = getCollectionPath(itemType);
    const itemsBindingContext = bindingContext
        .navigate(PrPayslipEntity.Employments)
        .addKey(EMPLOYEES_TEMPLATE_ID)
        .navigate(collectionPath);

    const rowsWithLocalFields = employeeEmploymentHolder?.[collectionPath];

    return rowsWithLocalFields
        .filter(row => {
            if (itemType === BulkAddItemType.ExtraSalary) {
                return !isHourlyExtraSalaryForCompensation(row);
            }
            if (itemType === BulkAddItemType.SalaryComponent) {
                return !isHourlySalaryComponentForCompensation(row);
            }
            return true;
        })
        .map((row) => {
            const employmentId = (row as IEntity)[commonBulkAddFieldPaths.Employment];

            const payslipItem = cleanEntity(row, itemsBindingContext.addKey(row), getImplicitFields(itemType));

            return {
                employmentId,
                payslipId: row?.["#id"]?.toString(),
                payslipItem,
            };
        });
};

export const updatePayslips = async (args: UpdatePayslipsArgs): Promise<boolean> => {
    const {storage, itemType} = args;
    if (args.data.length === 0) {
        logger.info("No data to update payslips");
        return true;
    }

    const collectionPath = getCollectionPath(itemType);
    const batch: BatchRequest = storage.oData.batch();
    batch.beginAtomicityGroup(BULK_ADD_ATOMICITY_GROUP);
    const payslipsWrapper = batch.getEntitySetWrapper(EntitySetName.PrPayslips);

    args.data.forEach((entry) => {
        payslipsWrapper.update(entry.payslipId, {
            [`${PrPayslipEntity.Employments}@odata.delta`]: [{
                [PrPayslipEmploymentEntity.Id]: String(entry.employmentId),
                [`${collectionPath}@odata.delta`]: [entry.payslipItem],
        }],
        });
    });

    try {
        const results = await batch.execute();
        const errors = results.filter(r => !isBatchResultOk(r)).map(result => result.body as ODataError);
        if (errors.length) {
            storage.setFormAlert(getBulkAddErrorAlert(errors, args.payslipTargets));
            storage.refresh();
            return false;
        }
    } catch (error) {
        storage.setFormAlert(getAlertFromError(error));
        storage.refresh();
        return false;
    }

    return true;
};
